// @flow

import React from "react";
import { localize } from "redux-i18n";
import Ajv from "ajv";

import { Button, Row, Col, SchemaForm, Text, View } from "@components";
import type { StepProps } from "@components";
import type { ReferralEmployeeSettings } from "@modules/service/reducer/referrals/types";
import { CommonReasonInputForm } from "@modules/service/components/";
import { RtwFormForm } from "@modules/service/components";
import { removeNullAndUndefinedProps } from "@transformers/object";
import type { ReferralReason } from "@modules/service/reducer/referrals/types";

type OwnProps = $Exact<{
  editing: boolean,
  schema: Object,
  draft: Object,
  data: ReferralEmployeeSettings,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...StepProps,
  ...PropsFromLocalize,
}>;

export class StepEmployeeReferrals extends React.Component<Props> {
  validator: *;
  errorMessage: string | boolean;

  get schema() {
    return this.props.schema || {};
  }

  get data() {
    return { ...this.props.data, declinedEscalation: true };
  }

  componentDidMount() {
    this.validator = new Ajv().compile(this.schema);
  }

  isValid = () => {
    if (this.validator) {
      let isValid = this.validator(
        removeNullAndUndefinedProps({
          ...this.data,
        }),
      );
      this.errorMessage = ((this.validator.errors || [])[0] || {}).message || false;

      if ((this.data.declinedReasons || []).filter(reason => reason.isActive === true).length < 1) {
        isValid = false;
        this.errorMessage = this.props.t("AT_LEAST_ONE_ACTIVE_REASON_MUST_BE_PROVIDED");
      }

      return isValid;
    }
    return false;
  };

  handleSubmit = () => {
    if (this.props.handleSubmit) {
      this.props.handleSubmit(this.data, true);
    }
  };

  updateNextStepsInstruction = ({ nextStepsInstruction }: { nextStepsInstruction: string }) => {
    this.props.onChange({ ...this.data, nextStepsInstruction }, true);
  };

  updateAlertConfigurations = (formData: Object) => {
    const allIds = [];
    const alertConfigurations = formData.filter((item: { id: number }) => {
      const isIn = allIds.indexOf(item.id);
      if (isIn === -1) {
        allIds.push(item.id);
      }
      return isIn === -1;
    });

    this.props.onChange({ ...this.data, alertConfigurations }, true);
  };
  updateDeclinedReasons = (declinedReasons: Array<ReferralReason>) => {
    this.props.onChange({ ...this.data, declinedReasons }, true);
  };
  updateFormQuestions = (formQuestions: Object) => {
    this.props.onChange({ ...this.data, formQuestions }, true);
  };

  get alertConfigurationsSchema() {
    const { alertConfigurations } = this.schema.properties;
    const { managerReferralsSettings } = this.props.draft;

    if (managerReferralsSettings && managerReferralsSettings.alertConfigurations) {
      const res = {
        ...alertConfigurations,
        items: {
          ...alertConfigurations.items,
          properties: {
            ...alertConfigurations.items.properties,
            id: {
              ...alertConfigurations.items.properties.id,
              oneOf: (alertConfigurations.items.properties.id.oneOf || []).filter(
                (item: { const: number, title: string }) =>
                  managerReferralsSettings.alertConfigurations
                    .map(ac => ac.id)
                    .indexOf(item.const) === -1,
              ),
            },
          },
        },
      };
      return res;
    }
    return alertConfigurations;
  }

  render() {
    if (!this.props.editable) {
      return <div />;
    }

    const { declinedReasons, formQuestions } = this.schema.properties;
    const isValid = this.isValid();
    return (
      <View gap={6}>
        {/*Alerts for Employee Led Referrals*/}
        <View gap={3} className="gs-container">
          <h6>{this.props.t("REFERRAL_DETAILS_ALERTS_FOR_EMPLOYEE_LED_REFERRALS")}</h6>
          {this.schema &&
            this.data &&
            this.alertConfigurationsSchema.items.properties.id.oneOf &&
            this.alertConfigurationsSchema.items.properties.id.oneOf.length > 0 && (
              <SchemaForm
                dontTransformTheData
                formData={this.data.alertConfigurations}
                data={this.data.alertConfigurations}
                schema={this.alertConfigurationsSchema}
                uiSchema={{
                  "ui:options": {
                    addButtonTitle: this.props.t("ADD_ALERT"),
                  },
                }}
                onChange={this.updateAlertConfigurations}
                externalSubmit
              />
            )}
          {this.alertConfigurationsSchema.items.properties.id.oneOf &&
            this.alertConfigurationsSchema.items.properties.id.oneOf.length < 1 && (
              <div className={"alert alert-danger"}>
                {this.props.t("PLEASE_REVIEW_THE_ALERT_LIST")}
              </div>
            )}
        </View>

        {/*Declined Referral*/}
        <View gap={3} className="gs-container">
          <h6>{this.props.t("DECLINED_REFERRAL")}</h6>

          <View padding={[0, 5, 0, 5]} gap={6}>
            <View>
              <SchemaForm
                externalSubmit
                dontTransformTheData
                onChange={this.updateNextStepsInstruction}
                formData={{ nextStepsInstruction: this.data.nextStepsInstruction }}
                schema={{
                  properties: {
                    nextStepsInstruction: {
                      type: ["string", "null"],
                      title: this.props.t("NEXT_STEPS_INSTRUCTIONS"),
                      maxLength: 2000,
                    },
                  },
                }}
                uiSchema={{
                  "ui:options": { hidelabel: true },
                  nextStepsInstruction: {
                    "ui:widget": "textarea",
                    "ui:options": {
                      rows: 4,
                      fieldPadding: "0.5rem 0",
                      labelposition: "top",
                    },
                  },
                }}
              />
            </View>
            <View>
              <Text type="h6">{this.props.t("REASONS_FOR_DECLINING")}</Text>
            </View>
          </View>
        </View>

        <View className="gs-container">
          {this.schema && this.data ? (
            <CommonReasonInputForm
              data={this.data.declinedReasons}
              schema={declinedReasons.items}
              onChange={this.updateDeclinedReasons}
            />
          ) : null}
        </View>

        {/*Referral Questions*/}
        <View gap={3} className="gs-container">
          <h6>{this.props.t("REFERRAL_QUESTIONS")}</h6>
          <RtwFormForm
            data={this.data.formQuestions}
            onChange={this.updateFormQuestions}
            schema={formQuestions.items}
          />
        </View>
        {!this.props.isCollapsable && (
          <div className="w-100 d-flex justify-content-end mt-5 mb-3">
            <Button onClick={this.handleSubmit} disabled={!isValid}>
              {this.props.t("NEXT_SUPPLIER_TRACKING")}
            </Button>
          </div>
        )}
      </View>
    );
  }
}

export default localize()(StepEmployeeReferrals);
